import { enqueueDialog, hasDialogHost, nextDialogId } from './dialogQueue';
import type {
  NxConfirmChoice,
  NxConfirmChoiceOptions,
  NxConfirmOptions,
  NxPromptOptions,
} from './types';

/** Title of the shorthand form `confirm('…')`. */
const DEFAULT_CONFIRM_TITLE = 'Bitte bestätigen';

export type NxConfirmFn = {
  /** Shorthand for `window.confirm(message)`: resolves `true` on confirm. */
  (message: string): Promise<boolean>;
  /** With a third action: resolves `'confirm'`, `'alternative'` or `'cancel'`. */
  (options: NxConfirmChoiceOptions): Promise<NxConfirmChoice>;
  /** Resolves `true` on confirm, `false` on cancel, Escape or backdrop. */
  (options: NxConfirmOptions): Promise<boolean>;
};

function browserConfirm(message: string): boolean {
  return typeof window !== 'undefined' && typeof window.confirm === 'function'
    ? window.confirm(message)
    : false;
}

/**
 * The kit's replacement for `window.confirm()`: an awaitable confirm dialog in
 * the app's look, rendered by the `NxConfirmHost` the shell mounts once.
 *
 * ```ts
 * const confirm = useNxConfirm();
 * if (await confirm({ title: 'Mount löschen?', message, confirmLabel: 'Löschen', tone: 'danger' })) …
 * ```
 *
 * Requests queue: a second confirm waits until the first is answered. Without
 * a mounted host it falls back to the browser's own `confirm()`, so a caller
 * never hangs.
 */
export function useNxConfirm(): NxConfirmFn {
  function confirm(input: string | NxConfirmOptions | NxConfirmChoiceOptions) {
    const options: NxConfirmOptions | NxConfirmChoiceOptions =
      typeof input === 'string' ? { title: DEFAULT_CONFIRM_TITLE, message: input } : input;
    const withChoice = 'alternativeLabel' in options && Boolean(options.alternativeLabel);

    if (!hasDialogHost()) {
      const text = [options.title, options.message].filter(Boolean).join('\n\n');
      const ok = browserConfirm(text);
      return Promise.resolve(withChoice ? (ok ? 'confirm' : 'cancel') : ok);
    }

    return new Promise<boolean | NxConfirmChoice>((resolve) => {
      enqueueDialog({
        kind: 'confirm',
        id: nextDialogId(),
        title: options.title,
        message: options.message,
        confirmLabel: options.confirmLabel,
        cancelLabel: options.cancelLabel,
        alternativeLabel: withChoice
          ? (options as NxConfirmChoiceOptions).alternativeLabel
          : undefined,
        tone: options.tone ?? 'default',
        resolve: (choice) => resolve(withChoice ? choice : choice === 'confirm'),
      });
    });
  }
  return confirm as NxConfirmFn;
}

/**
 * The kit's replacement for `window.prompt()`: an awaitable dialog with one
 * text field, rendered by `NxConfirmHost`. Resolves the entered text, or
 * `null` when cancelled.
 *
 * ```ts
 * const prompt = useNxPrompt();
 * const name = await prompt({ title: 'Ordner umbenennen', value: folder.name, validate });
 * ```
 *
 * Without a mounted host it falls back to the browser's `prompt()`; an answer
 * that `required` or `validate` refuses resolves `null` there.
 */
export function useNxPrompt(): (options: NxPromptOptions) => Promise<string | null> {
  return (options) => {
    const required = options.required !== false;
    if (!hasDialogHost()) {
      if (typeof window === 'undefined' || typeof window.prompt !== 'function') {
        return Promise.resolve(null);
      }
      const raw = window.prompt(options.message ?? options.title, options.value ?? '');
      if (raw === null) return Promise.resolve(null);
      const answer = raw.trim();
      if (required && !answer) return Promise.resolve(null);
      return Promise.resolve(options.validate?.(raw) ? null : answer);
    }
    return new Promise<string | null>((resolve) => {
      enqueueDialog({
        kind: 'prompt',
        id: nextDialogId(),
        title: options.title,
        message: options.message,
        label: options.label,
        value: options.value ?? '',
        placeholder: options.placeholder,
        confirmLabel: options.confirmLabel,
        cancelLabel: options.cancelLabel,
        required,
        validate: options.validate,
        resolve,
      });
    });
  };
}
